import { Button, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components'
import { Navigation24Regular } from '@fluentui/react-icons'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { useGetSearchParam, useSessionStorage } from '@/hooks'
import { ConfirmDialog } from '@/ui/Dialog/ConfirmDialog'
import { AddUserToOrgForm } from '@/ui/Forms/AddUserToOrgForm'
import { OverlayDrawerComponent } from '@/ui/OverlayDrawerComponent/OverlayDrawerComponent'
import { isProjectAdmin, isProjectLeader, isPlatformAdmin, isSuperUser } from '@/util'
import { toast } from 'react-toastify'

type MessagesSubscriptionsOrganisationUser = RestSchemas['Messages.Subscriptions.OrganisationUser']

export function RowActionsMenu({
  user,
  refetchOrganisationUsers,
}: {
  user: MessagesSubscriptionsOrganisationUser
  refetchOrganisationUsers: () => void
}) {
  const orgId = useGetSearchParam<number>('orgId')
  const [userRole] = useSessionStorage('roles', 1)
  const [editDrawerOpen, setEditDrawerOpen] = useState(false)
  const closeDrawer = useCallback(() => setEditDrawerOpen(false), [])
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const queryClient = useQueryClient()

  const isDeletePermitted = (userRole: number) =>
    ((isProjectAdmin(userRole) || isProjectLeader(userRole)) && userRole >= user.roles) ||
    isPlatformAdmin(userRole) ||
    isSuperUser(userRole)

  const isEndSessionPermitted = (userRole: number) =>
    isProjectLeader(userRole) || isPlatformAdmin(userRole) || isSuperUser(userRole)

  const isFreezePermitted = (userRole: number) =>
    ((isProjectAdmin(userRole) || isProjectLeader(userRole)) && userRole >= user.roles) ||
    isPlatformAdmin(userRole) ||
    isSuperUser(userRole)

  const isUnfreezePermitted = (userRole: number) => isPlatformAdmin(userRole) || isSuperUser(userRole)

  const {
    getSubscriptionsApiListOrganisationUsers,
    deleteSubscriptionsApiDeleteUser,
    postSubscriptionsApiFreezeUser,
    postSubscriptionsApiUnfreezeUser,
    postAuthenticationApiLogoutUser,
  } = createRestClient()

  const { mutate: deleteUser } = deleteSubscriptionsApiDeleteUser.useMutation(
    { query: { userId: user.id } }, // The path parameter
    {
      onSuccess: () => {
        toast.success('User disabled successfully.')
        onMutationSuccess()
      },
      onError: () => {
        toast.error('Failed to disable user.')
      },
    }
  )

  const { mutate: freezeUser } = postSubscriptionsApiFreezeUser.useMutation(
    { query: { userId: user.id } }, // The path parameter
    {
      onSuccess: () => {
        toast.success('User frozen successfully.')
        onMutationSuccess()
      },
      onError: () => {
        toast.error('Failed to freeze user.')
      },
    }
  )

  const { mutate: unfreezeUser } = postSubscriptionsApiUnfreezeUser.useMutation(
    { query: { userId: user.id } }, // The path parameter
    {
      onSuccess: () => {
        toast.success('User unfrozen successfully.')
        onMutationSuccess()
      },
      onError: () => {
        toast.error('Failed to ufreeze user.')
      },
    }
  )

  const { mutate: endUserSession } = postAuthenticationApiLogoutUser.useMutation(
    { path: { userId: user.id } }, // The path parameter
    {
      onSuccess: () => {
        toast.success('User session ended successfully.')
        onMutationSuccess()
      },
      onError: () => {
        toast.error('Failed to end user session.')
      },
    }
  )

  const onMutationSuccess = () => {
    getSubscriptionsApiListOrganisationUsers.invalidateQueries({ parameters: { query: { orgId } } }, queryClient)
  }

  return (
    <>
      <Menu
        open={isMenuOpen}
        onOpenChange={(_e, { open }) => {
          setIsMenuOpen(open)
        }}
      >
        <MenuTrigger disableButtonEnhancement>
          <Button icon={<Navigation24Regular />} />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem
              onClick={() => {
                setEditDrawerOpen(true)
              }}
            >
              Edit user
            </MenuItem>
            {isDeletePermitted(userRole) && (
              <MenuItem persistOnClick>
                <ConfirmDialog
                  trigger={<div>Delete user</div>}
                  title="Delete user"
                  content="Please confirm that the user should be removed from the organisation?"
                  onConfirm={() => deleteUser()}
                  onClose={() => setIsMenuOpen(false)}
                />
              </MenuItem>
            )}
            {/* Hide Freeze button if user is already frozen */}
            {isFreezePermitted(userRole) && !user.isFrozen && (
              <MenuItem persistOnClick>
                <ConfirmDialog
                  trigger={<div>Freeze user</div>}
                  title="Freeze user"
                  content="Please confirm that the user should be frozen for the organisation?"
                  onConfirm={() => freezeUser()}
                  onClose={() => setIsMenuOpen(false)}
                />
              </MenuItem>
            )}
            {/* Hide Unfreeze button if user is not frozen */}
            {isUnfreezePermitted(userRole) && user.isFrozen && (
              <MenuItem persistOnClick>
                <ConfirmDialog
                  trigger={<div>Unfreeze user</div>}
                  title="Unfreeze user"
                  content="Please confirm that the user should be unfrozen for the organisation?"
                  onConfirm={() => unfreezeUser()}
                  onClose={() => console.log('before close')}
                />
              </MenuItem>
            )}
            {isEndSessionPermitted(userRole) && (
              <MenuItem persistOnClick>
                <ConfirmDialog
                  trigger={<div>End user session</div>}
                  title="End user session"
                  content="Please confirm ending the user's session?"
                  onConfirm={() => endUserSession()}
                  onClose={() => setIsMenuOpen(false)}
                />
              </MenuItem>
            )}
          </MenuList>
        </MenuPopover>
      </Menu>

      <OverlayDrawerComponent
        open={editDrawerOpen}
        setOpen={setEditDrawerOpen}
        title={'Edit user'}
        content={
          <AddUserToOrgForm
            onSubmit={closeDrawer}
            edit
            selectedUser={user}
            refetchOrganisationUsers={refetchOrganisationUsers}
          />
        }
      />
    </>
  )
}
